import { FC, MouseEvent, useEffect, useState } from 'react';
import { useStudentFeesState } from '../../contexts/studentFees.context';
import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { isEmpty, values } from 'lodash-es';
import { StudentFeeMap, TStudentFeeSummaryMap } from './studentFees.interfaces';
import { GridColDef } from '@mui/x-data-grid';
import { TStudentFeeViewType } from '../../../../../apis/admissions/adminssionFees.interfaces';
import RowActions from '../../../../components/RowActions';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { useStudentFiltersState } from '../../contexts/studentFilters.context';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import { useResponsive } from '../../../../../hooks/useResponsiveHook';

interface StudentFeesGridProps {
  onRowClick: (data: TStudentFeeSummaryMap) => void;
  isLoading?: boolean;
}

const StudentFeesGrid: FC<StudentFeesGridProps> = ({
  onRowClick,
  isLoading = false,
}) => {
  const { translateText } = useTranslation();
  const { isMobile } = useResponsive();
  const { studentFeesRecords, simplifiedStudentObject } = useStudentFeesState();
  const filters = useStudentFiltersState();
  const [feesViewType, setFeesViewType] = useState('all');
  const [filteredFees, setFilteredFees] = useState<TStudentFeeSummaryMap[]>([]);

  const handleFeesViewChange = (
    _: MouseEvent<HTMLElement>,
    newFeesViewType: string
  ) => {
    setFeesViewType(newFeesViewType);
  };

  const onRowClickHandler = (data: TStudentFeeSummaryMap) => {
    onRowClick(data as TStudentFeeSummaryMap);
  };

  const getFeesGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'feeId',
        headerName: translateText('students.fees.feeId'),
        width: 160,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feeId}`;
        },
      },
      {
        field: 'basicDetails.name',
        headerName: translateText('students.fees.name'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.basicDetails?.firstName} ${data?.basicDetails?.middleName} ${data?.basicDetails?.lastName}`;
        },
      },
      {
        field: 'feeAmount',
        headerName: translateText('students.fees.feeAmount'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feeAmount}`;
        },
      },
      {
        field: 'feePaid',
        headerName: translateText('students.fees.feePaid'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feePaid}`;
        },
      },
      {
        field: 'feeDue',
        headerName: translateText('students.fees.feeDue'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feeDue}`;
        },
      },
      {
        field: 'feeDueDate',
        headerName: translateText('students.fees.feeDueDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feeDueDate}`;
        },
      },
      {
        field: 'feePaidDate',
        headerName: translateText('students.fees.feePaidDate'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.feePaidDate}`;
        },
      },
      {
        field: 'status',
        headerName: translateText('common.status'),
        width: 150,
        renderCell: (params) => {
          const data = params.row as TStudentFeeSummaryMap;
          return `${data?.fees[0]?.status}`;
        },
      },
      {
        field: 'actions',
        headerName: translateText('common.actions'),
        width: 150,
        renderCell: (params) => (
          <RowActions record={params} onRowClickHandler={onRowClickHandler} />
        ),
      },
    ];
  };

  useEffect(() => {
    const getFeesGridRowsToRender = () => {
      if (isEmpty(studentFeesRecords)) return [];
      const mapOfUsersFees: StudentFeeMap = {};

      studentFeesRecords.forEach((fee) => {
        const studentDetails = simplifiedStudentObject[fee.userId];
        if (
          (!filters.grade ||
            filters.grade === 'select' ||
            studentDetails?.grade === filters.grade) &&
          (!filters.section ||
            filters.section === 'select' ||
            studentDetails?.section === filters.section)
        ) {
          if (!mapOfUsersFees[fee.userId]) {
            mapOfUsersFees[fee.userId] = {
              id: fee.userId,
              basicDetails: simplifiedStudentObject?.[fee.userId]?.basicDetails, // TODO: this mapping should have worked
              fees: [fee],
            } as TStudentFeeSummaryMap;
          }
        }
      });

      setFilteredFees(values(mapOfUsersFees));
    };
    getFeesGridRowsToRender();
  }, [studentFeesRecords, simplifiedStudentObject, filters]);

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'space-between' },
          mb: { xs: 1, sm: 2 },
        }}
        data-testid="student-fees-view-toggle-container"
      >
        <ToggleButtonGroup
          size={isMobile ? 'small' : 'medium'}
          color="primary"
          exclusive
          value={feesViewType}
          onChange={handleFeesViewChange}
          aria-label={translateText('students.fees.feesView')}
          sx={{
            height: { xs: 'auto', sm: 'var(--tab-group-height)' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' },
          }}
          data-testid="student-fees-view-toggle-button-group"
        >
          <ToggleButton
            value={TStudentFeeViewType.ALL}
            aria-label={translateText('students.fees.allFees')}
            data-testid="student-fees-view-toggle-button-all"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: 'inherit' },
              flex: { xs: '1 1 33%', sm: 'none' },
            }}
          >
            {translateText('students.fees.showAllFees')}
          </ToggleButton>
          <ToggleButton
            value={TStudentFeeViewType.UNPAID}
            aria-label={translateText('students.fees.onlyDue')}
            data-testid="student-fees-view-toggle-button-due"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: 'inherit' },
              flex: { xs: '1 1 33%', sm: 'none' },
            }}
          >
            {translateText('students.fees.showOnlyDue')}
          </ToggleButton>
          <ToggleButton
            value={TStudentFeeViewType.PAID}
            aria-label={translateText('students.fees.allPaid')}
            data-testid="student-fees-view-toggle-button-paid"
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: 'inherit' },
              flex: { xs: '1 1 33%', sm: 'none' },
            }}
          >
            {translateText('students.fees.showAllPaid')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          height: `calc(100% - var(--tab-group-height))`,
          overflow: 'auto',
        }}
        data-testid="student-fees-grid-container"
      >
        <PaginatedGrid
          dataTestId="student-fees-grid"
          rows={filteredFees.filter((fee) => {
            if (feesViewType === TStudentFeeViewType.UNPAID) {
              return (
                fee.fees[0].status !== TStudentFeeViewType.PAID &&
                fee.fees[0].status !== TStudentFeeViewType.ALL
              );
            } else if (feesViewType === TStudentFeeViewType.PAID) {
              return fee.fees[0].status === TStudentFeeViewType.PAID;
            }
            return true;
          })}
          columns={getFeesGridColumns()}
          hideColumnsOnMobile={['feeId', 'feeDueDate', 'feePaidDate']}
        />
      </Grid>
    </Grid>
  );
};

export default StudentFeesGrid;
